"use client";

import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  Button,
  SkeletonBlock,
  SkeletonLoader,
  Text,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import { ManagedConnectionSource } from "@zapier/shared";
import styles from "./CreateConnectionAction.module.css";

const MENU_ITEM_PERSONAL = "personal";
const MENU_ITEM_MANAGED = "managed";

const CREATE_CONNECTION_MENU_ITEMS = [
  {
    id: MENU_ITEM_PERSONAL,
    icon: "personKey",
    titleKey: "menu-items.personal",
    descriptionKey: "menu-items.personal-description",
  },
  {
    id: MENU_ITEM_MANAGED,
    icon: "miscSecurity",
    titleKey: "menu-items.managed",
    descriptionKey: "menu-items.managed-description",
  },
] as const;

interface CreateConnectionActionProperty {
  // Renders a skeleton until the permissions that decide between the plain
  // and split button have resolved.
  isLoading?: boolean;
  // Caller folds in the split treatment and the user's role/account
  // permissions.
  showManagedOption: boolean;
  onAddConnection: () => void;
  onAddManagedConnection: (source: ManagedConnectionSource) => void;
}

export function CreateConnectionAction({
  isLoading = false,
  showManagedOption,
  onAddConnection,
  onAddManagedConnection,
}: CreateConnectionActionProperty) {
  const t = useTranslations("connections-header");

  if (isLoading) {
    return (
      <SkeletonLoader>
        <SkeletonBlock width={180} height={40} borderRadius="medium" />
      </SkeletonLoader>
    );
  }

  if (!showManagedOption) {
    return (
      <Button variant="primary" size="medium" onPress={onAddConnection}>
        {t("buttons.create-connection")}
      </Button>
    );
  }

  return (
    <BetaMenuTriggerRoot>
      <Button variant="primary" size="medium" iconPosition="end">
        {t("buttons.create-connection")}
        <Icon name="arrowSmallDown" aria-hidden={true} />
      </Button>
      <BetaMenuTriggerPopover placement="bottom end">
        <BetaMenuTriggerList
          aria-label={t("labels.create-connection-menu")}
          onAction={(key) => {
            if (key === MENU_ITEM_PERSONAL) {
              onAddConnection();
            } else if (key === MENU_ITEM_MANAGED) {
              onAddManagedConnection(ManagedConnectionSource.HeaderMenu);
            }
          }}
        >
          {CREATE_CONNECTION_MENU_ITEMS.map(
            ({ id, icon, titleKey, descriptionKey }) => (
              <BetaMenuTriggerItem
                key={id}
                id={id}
                textValue={t(titleKey)}
                className={styles.menuItemRow}
              >
                <span className={styles.menuItem}>
                  <Icon name={icon} size="20" aria-hidden={true} />
                  <span className={styles.menuItemText}>
                    <Text
                      type="MinimalPrint3Multiline"
                      tag="div"
                      className={styles.menuItemTitle}
                    >
                      {t(titleKey)}
                    </Text>
                    <Text
                      type="MinimalPrint1Multiline"
                      color="TextWeaker"
                      tag="div"
                      className={styles.menuItemDescription}
                    >
                      {t(descriptionKey)}
                    </Text>
                  </span>
                </span>
              </BetaMenuTriggerItem>
            ),
          )}
        </BetaMenuTriggerList>
      </BetaMenuTriggerPopover>
    </BetaMenuTriggerRoot>
  );
}
